import { useCallback, useRef, useState } from 'react';
import { fetchForecast, geocodeCity, reverseGeocodeCity } from './client';
import { buildWx } from './insightDeck';
import type { SkyMode, SkyNoteKind, Wx } from './types';

export type UseWeatherFlowResult = {
  mode: SkyMode;
  note: string;
  noteKind: SkyNoteKind;
  fetching: boolean;
  /** Bumped on a failed city search — bind to a shake animation. */
  shakeToken: number;
  requestGeo: () => void;
  openCityEntry: () => void;
  submitCity: (query: string) => void;
  skip: () => void;
};

/**
 * Geolocation or a typed city, calling `onResolved(wx)` exactly once (`null` on
 * skip). Every failure path must set a note that names the next action.
 */
export function useWeatherFlow(
  onResolved: (wx: Wx | null) => void,
): UseWeatherFlowResult {
  const [mode, setMode] = useState<SkyMode>('buttons');
  const [note, setNoteText] = useState('');
  const [noteKind, setNoteKind] = useState<SkyNoteKind>('idle');
  const [fetching, setFetching] = useState(false);
  const [shakeToken, setShakeToken] = useState(0);
  const enteredRef = useRef(false);

  const setNote = useCallback((text: string, kind: SkyNoteKind = 'idle') => {
    setNoteText(text);
    setNoteKind(kind);
  }, []);

  const cityMode = useCallback(
    (msg: string) => {
      setMode('city');
      setNote(msg, msg ? 'error' : 'idle');
    },
    [setNote],
  );

  const loadWeather = useCallback(
    async (lat: number, lon: number, knownCity: string | null) => {
      try {
        const [forecast, city] = await Promise.all([
          fetchForecast(lat, lon),
          knownCity ? Promise.resolve(knownCity) : reverseGeocodeCity(lat, lon),
        ]);
        const wx = buildWx(forecast, city || '');
        setFetching(false);
        enteredRef.current = true;
        onResolved(wx);
      } catch {
        setFetching(false);
        setNote('The sky is not answering. Try again, or skip.', 'error');
      }
    },
    [onResolved, setNote],
  );

  const requestGeo = useCallback(() => {
    if (fetching || enteredRef.current) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setNote("You're offline. Reconnect, or skip.", 'error');
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      cityMode('No location on this device. Type a city instead.');
      return;
    }
    setNote('LISTENING TO YOUR SKY…', 'busy');
    setFetching(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        void loadWeather(pos.coords.latitude, pos.coords.longitude, null);
      },
      (err) => {
        setFetching(false);
        cityMode(
          err.code === 1
            ? 'Location was blocked. Type a city instead.'
            : 'Your sky is out of reach. Type a city instead.',
        );
      },
      { timeout: 10000, maximumAge: 600000 },
    );
  }, [fetching, cityMode, loadWeather, setNote]);

  const openCityEntry = useCallback(() => {
    if (fetching || enteredRef.current) return;
    cityMode('');
  }, [fetching, cityMode]);

  const submitCity = useCallback(
    (query: string) => {
      if (fetching || enteredRef.current) return;
      const q = query.trim();
      if (!q) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        setNote("You're offline. Reconnect, or skip.", 'error');
        return;
      }
      setNote('LISTENING TO YOUR SKY…', 'busy');
      setFetching(true);
      (async () => {
        try {
          const result = await geocodeCity(q);
          if (!result) {
            setFetching(false);
            setShakeToken((t) => t + 1);
            setNote('No sky found by that name. Try another.', 'error');
            return;
          }
          await loadWeather(result.latitude, result.longitude, result.name);
        } catch {
          setFetching(false);
          setNote('The sky is not answering. Try again, or skip.', 'error');
        }
      })();
    },
    [fetching, loadWeather, setNote],
  );

  const skip = useCallback(() => {
    if (fetching || enteredRef.current) return;
    enteredRef.current = true;
    onResolved(null);
  }, [fetching, onResolved]);

  return {
    mode,
    note,
    noteKind,
    fetching,
    shakeToken,
    requestGeo,
    openCityEntry,
    submitCity,
    skip,
  };
}
